import React from "react";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-ink p-8 text-center">
                    <div>
                        <h1 className="font-display text-2xl font-semibold text-white">
                            Something went wrong
                        </h1>
                        <p className="mt-2 text-sm text-slate-400">
                            Please refresh the page.
                        </p>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollManager() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                const t = setTimeout(
                    () =>
                        el.scrollIntoView({ behavior: "smooth", block: "start" }),
                    80,
                );
                return () => clearTimeout(t);
            }
        }
        window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

function App() {
    const lenisRef = useRef(null);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <ScrollManager />
                <div className="relative min-h-screen bg-ink font-body text-white">
                    <Navbar />
                    <main>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="*" element={<Home />} />
                        </Routes>
                    </main>
                    <Footer />
                    <div className="noise-overlay" aria-hidden="true" />
                    <Toaster
                        position="bottom-right"
                        theme="dark"
                        richColors
                        toastOptions={{
                            style: {
                                background: "#131B2A",
                                border: "1px solid rgba(255,255,255,0.1)",
                                color: "#F8FAFC",
                            },
                        }}
                    />
                </div>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
